import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent } from 'lib-trend-core';

export interface MonitoringPreferences {
  alertsEnabled: boolean;
  delayThresholdSeconds: number;
  soundAlertEnabled: boolean;
}

export const DEFAULT_MONITORING_PREFERENCES: MonitoringPreferences = {
  alertsEnabled: true,
  delayThresholdSeconds: 300,
  soundAlertEnabled: false,
};

const MIN_SECONDS = 30;

@Component({
    selector: 'app-monitoring-settings-dialog',
    templateUrl: './monitoring-settings-dialog.component.html',
    styleUrls: ['./monitoring-settings-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MonitoringSettingsDialogComponent extends AbstractComponent implements OnInit {
  public alertsEnabled = DEFAULT_MONITORING_PREFERENCES.alertsEnabled;
  public soundAlertEnabled = DEFAULT_MONITORING_PREFERENCES.soundAlertEnabled;
  public minutes = Math.round(DEFAULT_MONITORING_PREFERENCES.delayThresholdSeconds / 60);

  private storageKey: string;

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<MonitoringSettingsDialogComponent>,
  ) {
    super(injector);
    this.storageKey = this.constants?.['monitoringPreferences'] ?? 'monitoringPreferences';
  }

  ngOnInit(): void {
    const prefs = this.handleMonitoringStorage('get') as MonitoringPreferences;
    this.alertsEnabled = prefs.alertsEnabled;
    this.soundAlertEnabled = prefs.soundAlertEnabled;
    this.minutes = Math.max(1, Math.round(prefs.delayThresholdSeconds / 60));
  }

  save(): void {
    const minutes = Number(this.minutes);
    if (!Number.isFinite(minutes) || minutes < 1) return;
    const prefs: MonitoringPreferences = {
      alertsEnabled: !!this.alertsEnabled,
      soundAlertEnabled: !!this.soundAlertEnabled,
      delayThresholdSeconds: Math.max(MIN_SECONDS, Math.floor(minutes * 60)),
    };
    this.handleMonitoringStorage('set', prefs);
    this.dialogRef.close(prefs);
  }

  close(): void {
    this.dialogRef.close(null);
  }

  private handleMonitoringStorage(action: 'get' | 'set' | 'remove', value?: MonitoringPreferences): MonitoringPreferences | null {
    try {
      if (action === 'get') {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return { ...DEFAULT_MONITORING_PREFERENCES };
        return { ...DEFAULT_MONITORING_PREFERENCES, ...JSON.parse(raw) };
      }
      if (action === 'remove') {
        localStorage.removeItem(this.storageKey);
        return null;
      }
      if (value) localStorage.setItem(this.storageKey, JSON.stringify(value));
      return null;
    } catch (err) {
      console.error(`Monitoring preferences storage operation failed: ${action}`, err);
      return null;
    }
  }
}
